/**
 * CSV rendering and download.
 *
 * Hand-written rather than pulled from a library: the output side of RFC 4180
 * is a dozen lines, and a dependency would still need the injection guard
 * below bolted onto it.
 */

/**
 * Fields that a spreadsheet would evaluate as a formula rather than read as
 * text. Group names come from WhatsApp and are therefore attacker-controlled:
 * anyone who adds one of our accounts to a group called
 * `=cmd|'/c calc'!A1` would otherwise plant that in an operator's spreadsheet.
 * Prefixing with an apostrophe is the standard neutralisation — Excel and
 * LibreOffice both read the rest as a literal string.
 */
function neutralize(field: string): string {
  return /^[=+\-@\t\r]/.test(field) ? `'${field}` : field;
}

/** Quotes a field if it could otherwise break the record structure. */
function escapeField(field: string): string {
  const safe = neutralize(field);
  if (/[",\r\n]/.test(safe)) {
    return `"${safe.replaceAll('"', '""')}"`;
  }
  return safe;
}

/** Renders rows as RFC 4180 CSV: CRLF line endings, doubled quotes. */
export function toCsv(rows: readonly (readonly string[])[]): string {
  return rows.map((row) => row.map(escapeField).join(",")).join("\r\n");
}

/**
 * Byte order mark. Without it Excel decodes the file as the system codepage and
 * mangles every non-ASCII group name; every other reader tolerates it.
 */
const BOM = "﻿";

/** Offers `csv` to the browser as a file download. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([BOM, csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Safari needs the URL to outlive the click by a tick; revoking in a
    // microtask would cancel the download it just started.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/** Makes an operator-supplied label safe to put in a filename. */
export function slugify(value: string, fallback = "account"): string {
  const slug = value
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-+|-+$/g, "")
    .slice(0, 40);
  return slug || fallback;
}
